import {
  Alert,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Flex,
  Grid,
  Skeleton,
  Tag,
  Typography
} from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../constants/placeholders"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"
import {
  FINANCE_ITEM_STATUSES,
  FINANCE_ITEM_STATUSES_MAP,
  type FinanceItemStatuses
} from "../../Project/ProjectViewPage/constants/finance-item-constants.ts"
import {
  INVOICE_STATUSES_MAP,
  type InvoiceStatuses
} from "../../Project/ProjectViewPage/constants/invoice-statuses.ts"
import {
  type InvoicePayment,
  useInvoicePayments
} from "../hooks/use-invoice-payments.ts"
import type { InvoiceListItem } from "../types/invoices.types.ts"

const { Text, Title } = Typography

type ViewableInvoice = ProjectInvoice | InvoiceListItem

interface InvoiceViewDrawerProps {
  invoice: ViewableInvoice | null
  onClose: () => void
}

const money = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === ""
    ? empty
    : budgetFormat(value)

const balanceOf = (invoice: ViewableInvoice) =>
  (Number(invoice.amount) || 0) - (Number(invoice.paid_to_date) || 0)

const SCHEDULED_STATUS_KEYS: Partial<Record<FinanceItemStatuses, string>> = {
  [FINANCE_ITEM_STATUSES.PLANNED]: "invoices.view.paymentStatus.planned",
  [FINANCE_ITEM_STATUSES.PENDING]: "invoices.view.paymentStatus.pending",
  [FINANCE_ITEM_STATUSES.PARTIAL]: "invoices.view.paymentStatus.partial"
}

interface PaymentRowProps {
  payment: InvoicePayment
  testId: string
  statusLabel?: string
}

function PaymentRow({ payment, testId, statusLabel }: PaymentRowProps) {
  const { timezone, dateFormat } = useDateFormatter()
  const statusStyle =
    FINANCE_ITEM_STATUSES_MAP[payment.status as FinanceItemStatuses]?.style
  return (
    <Flex justify="space-between" gap="small" data-testid={testId}>
      <Flex vertical>
        <Text>
          {payment.payment_date
            ? dayjs.utc(payment.payment_date).tz(timezone).format(dateFormat)
            : empty}
        </Text>
        <Text type="secondary" className="text-xs">
          {[payment.payment_method_display, payment.lineName]
            .filter(Boolean)
            .join(" · ") || empty}
        </Text>
      </Flex>
      <Flex gap="small" align="center">
        {statusLabel && (
          <Tag style={statusStyle} className="m-0">
            {statusLabel}
          </Tag>
        )}
        <Text>{money(payment.value)}</Text>
      </Flex>
    </Flex>
  )
}

/**
 * Every invoice the caller can list, read-only: its header, the lines it
 * bills, the payments against it and what is still owed. Nothing here writes.
 */
export function InvoiceViewDrawer({
  invoice,
  onClose
}: InvoiceViewDrawerProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const { loading, payments, scheduled, incomplete } =
    useInvoicePayments(invoice)
  const scheduledLabel = (payment: InvoicePayment) => {
    const key = SCHEDULED_STATUS_KEYS[payment.status as FinanceItemStatuses]
    return key ? t(key) : payment.status_display || empty
  }

  const statusStyle = invoice
    ? INVOICE_STATUSES_MAP?.[invoice.status as InvoiceStatuses]?.style
    : undefined
  const lines = invoice?.lines ?? []
  const projectLabel =
    (invoice as InvoiceListItem | null)?.project_info?.lead_name ||
    invoice?.project_name

  return (
    <Drawer
      title={t("invoices.view.title", {
        number: invoice?.invoice_number || ""
      })}
      placement="right"
      width={screens.md ? 520 : "100%"}
      open={Boolean(invoice)}
      onClose={onClose}
      destroyOnHidden
    >
      {invoice && (
        <Flex vertical gap="middle" data-testid="invoice-view-drawer">
          <Flex justify="space-between" align="center" gap="small" wrap>
            <Title level={5} className="m-0">
              {invoice.invoice_number || empty}
            </Title>
            <Flex gap={4}>
              {invoice.origin_display && (
                <Tag className="m-0">{invoice.origin_display}</Tag>
              )}
              <Tag style={statusStyle} className="m-0">
                {invoice.status_display || empty}
              </Tag>
            </Flex>
          </Flex>

          <Descriptions column={1} size="small" colon={false}>
            <Descriptions.Item label={t("invoices.columns.customer")}>
              {invoice.customer_name || empty}
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.columns.project")}>
              {projectLabel || empty}
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.columns.dateIssued")}>
              {invoice.date_issued || empty}
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.columns.dueDate")}>
              {invoice.due_date || empty}
            </Descriptions.Item>
          </Descriptions>

          <Divider className="my-0" />
          <Text strong>{t("invoices.view.lines")}</Text>
          {lines.length ? (
            <Flex vertical gap={4}>
              {lines.map((line) => (
                <Flex
                  key={line.id ?? line.finance_line}
                  justify="space-between"
                  gap="small"
                  data-testid="invoice-view-line"
                >
                  <Text ellipsis>{line.finance_line_name || empty}</Text>
                  <Text>{money(line.amount)}</Text>
                </Flex>
              ))}
            </Flex>
          ) : (
            <Text type="secondary">{t("invoices.view.noLines")}</Text>
          )}

          <Descriptions column={1} size="small" colon={false}>
            {invoice.subtotal != null && (
              <Descriptions.Item label={t("invoices.view.subtotal")}>
                {money(invoice.subtotal)}
              </Descriptions.Item>
            )}
            {Number(invoice.tax_amount) > 0 && (
              <Descriptions.Item label={t("invoices.view.tax")}>
                {money(invoice.tax_amount)}
              </Descriptions.Item>
            )}
            {Number(invoice.retainage_amount) > 0 && (
              <Descriptions.Item label={t("invoices.view.retainage")}>
                -{money(invoice.retainage_amount)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t("invoices.columns.amount")}>
              <Text strong data-testid="invoice-view-amount">
                {money(invoice.amount)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.columns.paidToDate")}>
              <Text data-testid="invoice-view-paid">
                {money(invoice.paid_to_date ?? 0)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.view.balance")}>
              <Text strong data-testid="invoice-view-balance">
                {money(balanceOf(invoice))}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          <Divider className="my-0" />
          <Text strong>{t("invoices.view.payments")}</Text>
          {loading ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          ) : (
            <>
              {incomplete && (
                <Alert
                  type="warning"
                  showIcon
                  message={t("invoices.view.paymentsUnavailable")}
                  data-testid="invoice-view-payments-unavailable"
                />
              )}
              {payments.length ? (
                <Flex vertical gap={4}>
                  {payments.map((payment) => (
                    <PaymentRow
                      key={payment.id}
                      payment={payment}
                      testId="invoice-view-payment"
                    />
                  ))}
                </Flex>
              ) : (
                !incomplete && (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("invoices.view.noPayments")}
                  />
                )
              )}
              {scheduled.length > 0 && (
                <Flex
                  vertical
                  gap={4}
                  data-testid="invoice-view-scheduled-payments"
                >
                  <Text strong>{t("invoices.view.scheduled")}</Text>
                  <Text type="secondary" className="text-xs">
                    {t("invoices.view.scheduledHint")}
                  </Text>
                  {scheduled.map((payment) => (
                    <PaymentRow
                      key={payment.id}
                      payment={payment}
                      testId="invoice-view-scheduled-payment"
                      statusLabel={scheduledLabel(payment)}
                    />
                  ))}
                </Flex>
              )}
            </>
          )}
        </Flex>
      )}
    </Drawer>
  )
}
